import React, { memo, useState } from 'react';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import CSSTransition from 'react-transition-group/CSSTransition';
import classNames from 'classnames';
import styles from '@lib/components/ReactHookForm/Form.module.scss';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import DragDrop from '@lib/assets/icons/dragDrop.svg?react';

const duration = 150;

const defaultStyle = {
  transition: `${duration}ms ease-in-out`,
  opacity: 0,
};

const transitionStyles = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

interface Props {
  children: React.ReactNode;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  errorsCount: number;
  isDraggable?: boolean;
  onRemove: () => void;
  title?: string;
}

function FieldArrayItemAccordion(props: Props) {
  const {
    children,
    errorsCount,
    isDraggable,
    title,
    dragHandleProps,
    onRemove,
  } = props;
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const [isAccordionOpen, setAccordionOpen] = useState(true);
  const toggleAccordion = () => setAccordionOpen((prevState) => !prevState);
  const handleAccordionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.defaultPrevented) return;
    toggleAccordion();
  };
  const accordionClassName = classNames(styles.subFieldAccordion, {
    [styles.subFieldAccordionWithErrors]: errorsCount,
    [styles.accordionClassNameDraggable]: isDraggable,
  });
  const dragProps = isDraggable && dragHandleProps ? dragHandleProps : {};
  return (
    <>
      <div
        className={classNames(styles.subFieldHead, {
          [styles.subFieldHeadOpened]: isAccordionOpen,
        })}
      >
        <button
          type="button"
          className={accordionClassName}
          onClick={handleAccordionClick}
        >
          <Typography variant="body" strong>
            {title}
          </Typography>
          {!!errorsCount && (
            <Typography variant="caption" className={styles.errorsCount}>
              {errorsCount}
            </Typography>
          )}
          <MaterialIcon
            icon="arrow_drop_down"
            className={styles.subFieldAccordionIcon}
          />
        </button>
        {isDraggable && (
          <div className={styles.subFieldAccordionDragDrop} {...dragProps}>
            <DragDrop />
          </div>
        )}
        <IconButton
          className={styles.subFieldDeleteBtn}
          icon="delete"
          onClick={onRemove}
          symbolsOutlined
          type={IconButtonTypes.PrimaryOutlined}
        />
      </div>
      <CSSTransition
        in={isAccordionOpen}
        timeout={duration}
        unmountOnExit
        nodeRef={nodeRef}
      >
        {(transitionState) => (
          <div
            style={{
              ...defaultStyle,
              // @ts-ignore
              ...transitionStyles[transitionState],
            }}
            className={styles.accordionContent}
          >
            {children}
          </div>
        )}
      </CSSTransition>
    </>
  );
}

export default memo(FieldArrayItemAccordion);
